import { useState, useRef, memo } from 'react';
import { Portal } from 'models';

import { Button, MenuItem } from '@material-ui/core';
import {
  Menu,
  DotsIcon,
  DefaultPortalIcon,
  MaintenancePortalIcon,
  InvestorPortalIcon,
} from 'components';

import clsx from 'clsx';
import { useStyles } from './style';

function getPortalIcon(portal: Portal) {
  switch (portal.Name) {
    case 'Default':
      return DefaultPortalIcon;
    case 'Investor':
      return InvestorPortalIcon;
    case 'Maintenance':
      return MaintenancePortalIcon;
    default:
      return null;
  }
}

interface Props {
  portals: Portal[];
  selectedPortal: Portal;
  handleChangePortal: (portal: Portal) => void;
}

const PortalPicker: React.FC<Props> = ({
  portals,
  selectedPortal,
  handleChangePortal,
}) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: React.MouseEvent<EventTarget> | void) => {
    if (event) {
      if (
        anchorRef.current &&
        anchorRef.current.contains(event.target as HTMLElement)
      ) {
        return;
      }
    }

    setOpen(false);
  };

  const handleItemClick = (portal: Portal) => {
    handleChangePortal(portal);
    setOpen(false);
  };

  return (
    <div className={classes.root}>
      <Button
        ref={anchorRef}
        onClick={handleToggle}
        classes={{
          root: classes.button,
        }}
        disableRipple
      >
        <DotsIcon
          className={clsx(classes.icon, {
            [classes.iconActive]: open,
          })}
        />
      </Button>
      <Menu
        open={open}
        handleClose={handleClose}
        anchorEl={anchorRef.current}
        PopperProps={{
          placement: 'bottom-end',
        }}
        PaperProps={{
          classes: {
            elevation1: classes.elevation1,
            rounded: classes.rounded,
          },
        }}
      >
        {portals.map((portal) => {
          const isActive = portal.Id === selectedPortal.Id;
          const Icon = getPortalIcon(portal);

          return (
            <MenuItem
              key={portal.Id}
              selected={isActive}
              onClick={() => handleItemClick(portal)}
              classes={{ root: classes.menuItem }}
            >
              {Icon ? <Icon className={classes.portalIcon} /> : null}
              <span className={classes.portalLabel}>{portal.Label}</span>
            </MenuItem>
          );
        })}
      </Menu>
    </div>
  );
};

export default memo(PortalPicker);
